import { useCallback, useEffect, useMemo } from 'react'

import { useDispatch } from 'react-redux'

import {
  openModal,
  closeModal,
  removeClosedModals,
  getModalKey,
} from 'src/slices/modal'
import type { ModalName } from 'src/slices/modal'

import { useSelector } from './useSelector'
import { useTheme } from '@mui/material'

const useModal = (name: ModalName, discriminator?: string) => {
  const dispatch = useDispatch()
  const modalKey = useMemo(
    () => getModalKey(name, discriminator),
    [name, discriminator]
  )

  const theme = useTheme()
  const defaultZIndex = theme.zIndex.modal

  const modalState = useSelector((state) => {
    return state.modal.modals?.[modalKey]
  })

  const { isOpen, data } = useMemo(() => {
    const state = modalState ?? { isOpen: false, data: {} }
    // todo: if there is a discriminator, we need to match it with the discriminator passed in.
    // this is to prevent the multiple instances of the modal getting the data for a discriminator specific modal
    if (!discriminator) {
      return state
    }

    if (state.data.discriminator === discriminator) {
      return state
    }

    return { isOpen: false, data: {} }
  }, [modalState])

  const openingOrder = useSelector((state) => state.modal.openingOrder)

  const zIndex = useMemo(() => {
    const index = openingOrder.findIndex(
      (modal) => modal.name === name && modal.discriminator === discriminator
    )
    return index >= 0 ? defaultZIndex + index : -1 // Return -1 if the modal is not found
  }, [openingOrder, name, discriminator, defaultZIndex])

  const handleOpen = useCallback(
    (data?: object | React.SyntheticEvent) => {
      if (data && (data as React.SyntheticEvent).currentTarget) {
        data = undefined // Ignore the event object
      }
      dispatch(openModal({ name: name, data: { discriminator, ...data } }))
    },
    [name, discriminator, dispatch]
  )

  const handleClose = useCallback(() => {
    dispatch(closeModal({ name: name, discriminator }))
  }, [name, discriminator, dispatch])

  useEffect(() => {
    if (!isOpen) {
      const timeoutId = setTimeout(() => {
        dispatch(removeClosedModals())
      }, 300) // Delay to match the closing animation duration

      return () => clearTimeout(timeoutId)
    }
  }, [isOpen, dispatch])

  return { handleOpen, handleClose, isOpen, data, zIndex, modalKey }
}

export default useModal
